import {DataModificationsHeader} from 'audit/DataModifications';
import {pluralize} from 'BenchlingUtil';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {
  ParameterStructuredTable,
  ParameterStructuredTableMetadata,
  ParameterStructuredTableRowMetadata,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {ParameterStructuredTableMode} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {ACTION_SUBMIT_PARAMETER} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {RowSubmissionRange} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {
  ActionResultType,
  ServerSideWriteActionResult,
  abortAction,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import {TableActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';

export const PARAMETER_TABLE_MODE_TO_OBJECT_NAME: Record<ParameterStructuredTableMode, string> = {
  [ParameterStructuredTableMode.PARAMETER_CONFIRMATION]: 'Parameter Confirmation',
  [ParameterStructuredTableMode.PARAMETER_ACTUAL]: 'Parameter Measurement',
  [ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION]: 'Material Input',
  [ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION]: 'Material Output',
  [ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION]: 'Equipment Confirmation',
};

type CreateParametersSubmitResult =
  | {
      resultType: ActionResultType.SUCCESS;
      responseData: ActionFunctionDAO.ParameterSubmitResponse;
    }
  | {resultType: 'BULK_ADAPTER_ERROR'; bulkAdapterErrorInfo: SpreadsheetValidationErrorInfo};

async function attemptCreateParameters(
  entryId: string,
  structuredTable: ParameterStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  dataModsHeader?: DataModificationsHeader
): Promise<CreateParametersSubmitResult> {
  const responseData = await ActionFunctionDAO.submitParameters(
    entryId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    dataModsHeader
  );
  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function doPerformCreateParameters(
  documentId: string,
  structuredTable: ParameterStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ParameterSubmitResponse>> {
  const pertinentComputedTable = selection
    ? computedTable.slice(selection.start, selection.end)
    : computedTable;

  const createParametersResult = await attemptCreateParameters(
    documentId,
    structuredTable,
    pertinentComputedTable,
    actionFunctionContext,
    selection,
    dataModsHeader
  );

  if (createParametersResult.resultType === 'BULK_ADAPTER_ERROR') {
    return abortAction();
  }

  return createParametersResult;
}

export async function onInvokeCreateParametersActionFunction(
  params: TableActionFunctionParameters<ParameterStructuredTableMetadata, ParameterStructuredTableRowMetadata>
): Promise<void> {
  const {
    structuredTable,
    structuredTableAdapter,
    computedTable,
    entryOrTemplateId,
    actionFunctionContext,
    table,
  } = params;
  const {mode} = structuredTable.metadata;
  const loadingScreenMessage = `Updating ${pluralize(
    structuredTable.rows.length,
    PARAMETER_TABLE_MODE_TO_OBJECT_NAME[mode]
  )}...`;
  await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.ParameterSubmitResponse>({
    entryId: entryOrTemplateId!,
    loadingScreenMessage,
    autoSaverAction: ACTION_SUBMIT_PARAMETER,
    doPerformAction: ({dataModsHeader}) =>
      doPerformCreateParameters(
        entryOrTemplateId!,
        structuredTable,
        computedTable,
        actionFunctionContext,
        undefined,
        dataModsHeader
      ),
    structuredTables: [structuredTable],
    structuredTableAdapters: [structuredTableAdapter],
    computedTables: [computedTable],
    tables: table ? [table] : [],
  });
}

export const submitParameterActionFunction = makeSimpleActionFunction<{}, {}>({
  label: (params) => getSubmitButtonLabel(params.tableStatus),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params.tableStatus, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeCreateParametersActionFunction,
  getDisabled: isTableActionButtonDisabled,
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
